import React, { useEffect, useRef, useState } from "react";
import { bankTransferApiService } from "../../../service/admin/bankTransferApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import { NetworkConfiguration } from "../../../network/networkConfriguation";
import "./style.css";

export default function BankTransferSettings() {
  const [form, setForm] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifsc: "",
    upi: "",
    gstNumber: "",
    showGstToUser: false,
    qrImageUrl: "",
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [qrPreview, setQrPreview] = useState("");
  const fileInputRef = useRef(null);
  const message = useMessage();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = () => {
    setLoading(true);
    bankTransferApiService
      .getBankTransferSettings()
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          const d = res.data;
          setForm({
            accountHolderName: d.accountHolderName || "",
            bankName: d.bankName || "",
            accountNumber: d.accountNumber || "",
            ifsc: d.ifsc || "",
            upi: d.upi || "",
            gstNumber: d.gstNumber || "",
            showGstToUser: !!d.showGstToUser,
            qrImageUrl: d.qrImageUrl || "",
            isActive: d.isActive !== false,
          });
          setQrPreview(d.qrImageUrl || "");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleQrFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.showToast("Only image files are supported for QR.", ToastTypes.ERROR);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      message.showToast("QR image must be under 3MB.", ToastTypes.ERROR);
      return;
    }
    setUploadingQr(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const loginHeader = getAuthLocalStorage()?.loginHeader;
      const response = await fetch(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.UPLOAD_RECHARGE_PROOF,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${loginHeader}` },
          body: formData,
        }
      );
      const data = await response.json();
      if (data?.success && data?.url) {
        setForm((prev) => ({ ...prev, qrImageUrl: data.url }));
        setQrPreview(data.url);
        message.showToast("QR image uploaded.", ToastTypes.SUCCESS);
      } else {
        message.showToast("Failed to upload QR image.", ToastTypes.ERROR);
      }
    } catch {
      message.showToast("Upload error. Try again.", ToastTypes.ERROR);
    } finally {
      setUploadingQr(false);
    }
  };

  const handleSave = () => {
    if (!form.accountNumber && !form.upi && !form.qrImageUrl) {
      message.showToast("Please fill in at least one payment detail.", ToastTypes.WARNING);
      return;
    }
    setSaving(true);
    bankTransferApiService
      .saveBankTransferSettings(form)
      .then((res) => {
        if (res?.status?.code === 200) {
          message.showToast("Bank transfer settings saved!", ToastTypes.SUCCESS);
        } else {
          message.showToast(res?.error?.message || "Failed to save.", ToastTypes.ERROR);
        }
      })
      .catch(() => message.showToast("Error saving settings.", ToastTypes.ERROR))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="bt-admin-page">
        <div className="bt-admin-loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="bt-admin-page">
      <div className="bt-admin-header">
        <h2 className="bt-admin-title">Bank Transfer Settings</h2>
        <p className="bt-admin-subtitle">
          Configure the bank account details shown to customers on the checkout page.
        </p>
      </div>

      <div className="bt-admin-card">
        <div className="bt-admin-form-grid">
          <div className="bt-admin-field">
            <label className="bt-admin-label">Account Holder Name</label>
            <input
              type="text"
              name="accountHolderName"
              className="bt-admin-input"
              placeholder="e.g. Importerr Pvt. Ltd."
              value={form.accountHolderName}
              onChange={handleChange}
            />
          </div>

          <div className="bt-admin-field">
            <label className="bt-admin-label">Bank Name</label>
            <input
              type="text"
              name="bankName"
              className="bt-admin-input"
              placeholder="e.g. HDFC Bank"
              value={form.bankName}
              onChange={handleChange}
            />
          </div>

          <div className="bt-admin-field">
            <label className="bt-admin-label">Account Number</label>
            <input
              type="text"
              name="accountNumber"
              className="bt-admin-input"
              placeholder="e.g. 1234567890"
              value={form.accountNumber}
              onChange={handleChange}
            />
          </div>

          <div className="bt-admin-field">
            <label className="bt-admin-label">IFSC Code</label>
            <input
              type="text"
              name="ifsc"
              className="bt-admin-input"
              placeholder="e.g. HDFC0001234"
              value={form.ifsc}
              onChange={handleChange}
            />
          </div>

          <div className="bt-admin-field">
            <label className="bt-admin-label">UPI ID</label>
            <input
              type="text"
              name="upi"
              className="bt-admin-input"
              placeholder="e.g. importerr@hdfc"
              value={form.upi}
              onChange={handleChange}
            />
          </div>

          <div className="bt-admin-field">
            <label className="bt-admin-label">GST Number</label>
            <input
              type="text"
              name="gstNumber"
              className="bt-admin-input"
              placeholder="e.g. 27ABCDE1234F1Z5"
              value={form.gstNumber}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* QR Image */}
        <div className="bt-admin-field bt-admin-field-full">
          <label className="bt-admin-label">QR Code Image</label>
          <div className="bt-admin-qr-row">
            {qrPreview && (
              <div className="bt-admin-qr-preview-wrap">
                <img src={qrPreview} alt="QR Code" className="bt-admin-qr-preview" />
                <button
                  className="bt-admin-qr-remove"
                  onClick={() => { setForm((p) => ({ ...p, qrImageUrl: "" })); setQrPreview(""); }}
                  type="button"
                >
                  Remove
                </button>
              </div>
            )}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleQrFileChange}
              />
              <button
                className="bt-admin-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingQr}
                type="button"
              >
                {uploadingQr ? "Uploading…" : qrPreview ? "Replace QR Image" : "Upload QR Image"}
              </button>
              <p className="bt-admin-hint">PNG or JPG, max 3MB</p>
            </div>
          </div>
        </div>

        {/* Active toggle */}
        <div className="bt-admin-toggle-row">
          <label className="bt-admin-toggle-label">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="bt-admin-checkbox"
            />
            <span>Enable Bank Transfer on checkout</span>
          </label>
        </div>

        <div className="bt-admin-toggle-row">
          <label className="bt-admin-toggle-label">
            <input
              type="checkbox"
              name="showGstToUser"
              checked={form.showGstToUser}
              onChange={handleChange}
              className="bt-admin-checkbox"
            />
            <span>Show GST Number to customers in checkout modal</span>
          </label>
        </div>

        <button className="bt-admin-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
